/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef } from 'react';
import {
    Table, Button, Tag, Modal, Form, Select, DatePicker,
    Input, Upload, message, Popconfirm, Spin, Tooltip
} from 'antd';
import {
    PlusOutlined, UploadOutlined, DeleteOutlined,
    FilePdfOutlined, FileOutlined, EyeOutlined,
    WarningOutlined, CheckCircleOutlined, ClockCircleOutlined
} from '@ant-design/icons';
import FleetService from '../../services/FleetService';
import FileService from '../../services/FileService';
import moment from 'moment';

const { Option } = Select;

// ── Expiry status badge ───────────────────────────────────────────────────────
const ExpiryBadge = ({ expiry }) => {
    if (!expiry) return <Tag color="default">No expiry date</Tag>;
    const days = moment(expiry).diff(moment(), 'days');
    if (days < 0)   return <Tag color="red"   icon={<WarningOutlined />}>EXPIRED {moment(expiry).format('DD MMM YYYY')}</Tag>;
    if (days < 30)  return <Tag color="orange" icon={<ClockCircleOutlined />}>Expires in {days}d</Tag>;
    if (days < 90)  return <Tag color="gold"  icon={<ClockCircleOutlined />}>{moment(expiry).format('DD MMM YYYY')}</Tag>;
    return <Tag color="green" icon={<CheckCircleOutlined />}>{moment(expiry).format('DD MMM YYYY')}</Tag>;
};

// ── File icon by extension ────────────────────────────────────────────────────
const FileIcon = ({ name }) => {
    const ext = (name || '').split('.').pop().toLowerCase();
    if (ext === 'pdf') return <FilePdfOutlined style={{ color: '#ff4d4f', fontSize: 16 }} />;
    return <FileOutlined style={{ color: '#1890ff', fontSize: 16 }} />;
};

// ── Main component ────────────────────────────────────────────────────────────
export default function VehicleDocumentsUpload({ vehicleId, clientId, visible }) {
    const [docs, setDocs]           = useState([]);
    const [loading, setLoading]     = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [saving, setSaving]       = useState(false);
    const [uploading, setUploading] = useState(false);
    const [uploadedFile, setUploadedFile] = useState(null); // { file_id, file_name, file_ext, url }
    const [form]                    = Form.useForm();

    useEffect(() => {
        if (vehicleId && visible) loadDocs();
    }, [vehicleId, visible]);

    const loadDocs = async () => {
        setLoading(true);
        try {
            const r = await FleetService.getDocuments(vehicleId);
            if (r.data.code === 200) setDocs(r.data.result || []);
        } catch (e) {
            message.error('Could not load documents');
        }
        setLoading(false);
    };

    // Handle file selection — upload immediately to get file_id
    const handleFileChange = async (info) => {
        const file = info.file.originFileObj || info.file;
        if (!file) return;

        // Validate type
        const allowed = ['pdf', 'jpg', 'jpeg', 'png', 'doc', 'docx'];
        const ext = file.name.split('.').pop().toLowerCase();
        if (!allowed.includes(ext)) {
            message.error(`File type .${ext} not allowed. Use: PDF, JPG, PNG, DOC, DOCX`);
            return;
        }
        // Validate size (10MB)
        if (file.size > 10 * 1024 * 1024) {
            message.error('File must be under 10MB');
            return;
        }

        setUploading(true);
        try {
            const res = await FileService.upload(file, clientId);
            if (res.data.code === 200 && res.data.file_id) {
                setUploadedFile({
                    file_id:   res.data.file_id,
                    file_name: res.data.file?.file_name || file.name,
                    file_ext:  res.data.file?.file_ext  || `.${ext}`,
                });
                message.success(`${file.name} uploaded successfully`);
            } else {
                message.error(res.data.message || 'Upload failed');
            }
        } catch (e) {
            message.error('Upload failed: ' + e.message);
        }
        setUploading(false);
    };

    const saveDoc = async (vals) => {
        setSaving(true);
        try {
            const payload = {
                vehicle_id:        vehicleId,
                doc_type:          vals.doc_type,
                issue_date:        vals.issue_date?.format('YYYY-MM-DD') || null,
                expiry_date:       vals.expiry_date?.format('YYYY-MM-DD') || null,
                issuing_authority: vals.issuing_authority || '',
                reference_no:      vals.reference_no || '',
                notes:             vals.notes || '',
                file_id:           uploadedFile?.file_id || null,
            };

            const r = await FleetService.saveDocument(payload);
            if (r.data.code === 200) {
                message.success('Document saved successfully');
                setModalOpen(false);
                form.resetFields();
                setUploadedFile(null);
                loadDocs();
            } else {
                message.error(r.data.message || 'Save failed');
            }
        } catch (e) {
            message.error('Save failed: ' + e.message);
        }
        setSaving(false);
    };

    const deleteDoc = async (id) => {
        try {
            const r = await FleetService.deleteDocument(id);
            if (r.data.code === 200) { message.success('Document removed'); loadDocs(); }
            else message.error(r.data.message);
        } catch (e) { message.error('Delete failed'); }
    };

    const openModal = () => {
        form.resetFields();
        setUploadedFile(null);
        setModalOpen(true);
    };

    // ── Summary badges for the collapsed view ────────────────────────────────
    const expired  = docs.filter(d => d.status === 'Expired').length;
    const expiring = docs.filter(d => d.status === 'Expiring Soon').length;
    const valid    = docs.filter(d => d.status === 'Valid').length;

    const cols = [
        {
            title: 'Document Type', dataIndex: 'doc_type', key: 'dt', width: 160,
            render: v => <strong>{v}</strong>
        },
        {
            title: 'Reference No', dataIndex: 'reference_no', key: 'ref', width: 130,
            render: v => v || <span style={{ color: '#bbb' }}>—</span>
        },
        {
            title: 'Issued By', dataIndex: 'issuing_authority', key: 'ia', width: 140,
            render: v => v || <span style={{ color: '#bbb' }}>—</span>
        },
        { title: 'Issue Date',  dataIndex: 'issue_date',  key: 'id', width: 110 },
        {
            title: 'Expiry',    dataIndex: 'expiry_date', key: 'ed', width: 180,
            render: (v, r) => <ExpiryBadge expiry={v} />
        },
        {
            title: 'File', key: 'file', width: 80,
            render: r => r.file_id ? (
                <Tooltip title="View / Download">
                    <Button type="link" size="small" icon={<FileIcon name={r.file_name} />}
                        href={`/ims/api/file/download/${r.file_id}`} target="_blank">
                        View
                    </Button>
                </Tooltip>
            ) : <span style={{ color: '#bbb', fontSize: 12 }}>No file</span>
        },
        {
            title: '', key: 'del', width: 50,
            render: r => (
                <Popconfirm title="Remove this document?" onConfirm={() => deleteDoc(r.id)}
                    okText="Remove" cancelText="Cancel" okButtonProps={{ danger: true }}>
                    <Button type="text" size="small" danger icon={<DeleteOutlined />} />
                </Popconfirm>
            )
        },
    ];

    return (
        <div style={{
            border: '1px solid #e6e6e6',
            borderRadius: 8,
            padding: '14px 16px',
            background: '#fafcff',
            marginTop: 8
        }}>
            {/* ── Header row ── */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontWeight: 600, fontSize: 14 }}>
                        📄 Vehicle Documents
                    </span>
                    {docs.length > 0 && (
                        <span style={{ fontSize: 12, color: '#888' }}>
                            ({docs.length} on file
                            {expired > 0  && <span style={{ color: '#ff4d4f',  marginLeft: 4 }}>· {expired} expired</span>}
                            {expiring > 0 && <span style={{ color: '#fa8c16',  marginLeft: 4 }}>· {expiring} expiring soon</span>}
                            {valid > 0    && <span style={{ color: '#52c41a',  marginLeft: 4 }}>· {valid} valid</span>}
                            )
                        </span>
                    )}
                </div>
                <Button type="primary" ghost size="small" icon={<PlusOutlined />} onClick={openModal}>
                    Add Document
                </Button>
            </div>

            {/* ── Document table ── */}
            <Spin spinning={loading}>
                {docs.length > 0 ? (
                    <Table
                        columns={cols}
                        dataSource={docs}
                        rowKey="id"
                        size="small"
                        pagination={false}
                        scroll={{ x: 800 }}
                        rowClassName={r =>
                            r.status === 'Expired'       ? 'doc-row-expired'  :
                            r.status === 'Expiring Soon' ? 'doc-row-expiring' : ''
                        }
                    />
                ) : (
                    <div style={{
                        textAlign: 'center', padding: '18px 0',
                        color: '#aaa', fontSize: 13, border: '1px dashed #e0e0e0',
                        borderRadius: 6, background: '#fff'
                    }}>
                        No documents uploaded yet — add the blue book, insurance, road licence or fitness certificate
                    </div>
                )}
            </Spin>

            {/* ── Row colour styles ── */}
            <style>{`
                .doc-row-expired td  { background: #fff1f0 !important; }
                .doc-row-expiring td { background: #fff7e6 !important; }
            `}</style>

            {/* ── Add Document Modal ── */}
            <Modal
                title={<span><PlusOutlined style={{ marginRight: 8 }} />Add Vehicle Document</span>}
                open={modalOpen}
                onCancel={() => { setModalOpen(false); setUploadedFile(null); }}
                footer={null}
                width={520}
                maskClosable={false}
                zIndex={1100}
            >
                <Form form={form} layout="vertical" onFinish={saveDoc}>

                    <Form.Item name="doc_type" label="Document Type"
                        rules={[{ required: true, message: 'Select document type' }]}>
                        <Select placeholder="Select type">
                            <Option value="Blue Book">🔵 Blue Book (Vehicle Registration)</Option>
                            <Option value="Road Licence">🚗 Road Licence / Disc</Option>
                            <Option value="Insurance">🛡️ Insurance Certificate</Option>
                            <Option value="Fitness Certificate">✅ Fitness Certificate</Option>
                            <Option value="Inspection Report">🔍 Inspection Report</Option>
                            <Option value="Other">📄 Other</Option>
                        </Select>
                    </Form.Item>

                    <Form.Item name="reference_no" label="Certificate / Reference Number">
                        <Input placeholder="e.g. INS-2024-00123" />
                    </Form.Item>

                    <Form.Item name="issuing_authority" label="Issuing Authority">
                        <Input placeholder="e.g. Road Transport Dept, Insurance Co." />
                    </Form.Item>

                    <div style={{ display: 'flex', gap: 12 }}>
                        <Form.Item name="issue_date" label="Issue Date" style={{ flex: 1 }}>
                            <DatePicker style={{ width: '100%' }} />
                        </Form.Item>
                        <Form.Item name="expiry_date" label="Expiry Date" style={{ flex: 1 }}>
                            <DatePicker style={{ width: '100%' }} />
                        </Form.Item>
                    </div>

                    {/* ── File upload ── */}
                    <Form.Item label="Upload Document (PDF, JPG, PNG, DOC)">
                        <Upload
                            beforeUpload={() => false}
                            onChange={handleFileChange}
                            maxCount={1}
                            accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                            showUploadList={false}
                        >
                            <Button icon={<UploadOutlined />} loading={uploading} style={{ marginBottom: 8 }}>
                                {uploading ? 'Uploading...' : 'Choose File'}
                            </Button>
                        </Upload>
                        {uploadedFile && (
                            <div style={{
                                display: 'flex', alignItems: 'center', gap: 8,
                                padding: '6px 10px', background: '#f6ffed',
                                border: '1px solid #b7eb8f', borderRadius: 6, marginTop: 4
                            }}>
                                <FileIcon name={uploadedFile.file_name} />
                                <span style={{ fontSize: 13, color: '#389e0d' }}>
                                    {uploadedFile.file_name}
                                </span>
                                <CheckCircleOutlined style={{ color: '#52c41a', marginLeft: 'auto' }} />
                            </div>
                        )}
                        {!uploadedFile && (
                            <div style={{ fontSize: 12, color: '#aaa', marginTop: 4 }}>
                                Optional — you can save the record without a file and upload later
                            </div>
                        )}
                    </Form.Item>

                    <Form.Item name="notes" label="Notes">
                        <Input.TextArea rows={2} placeholder="Any additional notes about this document" />
                    </Form.Item>

                    <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                        <Button onClick={() => { setModalOpen(false); setUploadedFile(null); }}>
                            Cancel
                        </Button>
                        <Button type="primary" htmlType="submit" loading={saving}>
                            Save Document
                        </Button>
                    </div>
                </Form>
            </Modal>
        </div>
    );
}